import { translateUi } from "@/shared/lib/ui-translate";
import * as Sentry from "@sentry/nextjs";
import { toast } from "sonner";
// Только тип: рантайм-импорт zod (~340 КБ) ехал в общий чанк каждой страницы,
// потому что обработчик ошибок нужен всем (KAN-974). Проверка — по форме ошибки.
import type { z } from "zod";

import { ERROR_HANDLING, API_STATUS_CODES } from "@/shared/config";
import { getErrorMessages } from "@/shared/lib/error-messages";
import { reportNetworkFailure } from "@/shared/lib/network";

import { shouldReportError } from "./should-report-error";

/**
 * ZodError без рантайм-импорта zod. zod 4 ставит ошибке name "ZodError" и массив
 * issues — у ошибок другой природы такого сочетания нет.
 */
function looksLikeZodError(error: unknown): error is z.ZodError {
  return (
    error instanceof Error &&
    error.name === "ZodError" &&
    Array.isArray((error as { issues?: unknown }).issues)
  );
}
import {
  extractServerFieldErrors,
  summarizeFieldErrors,
  type ServerFieldErrors,
} from "@/shared/api/validation-errors";

/**
 * Достаёт человекочитаемое сообщение бизнес-ошибки из ответа бэка
 * (`response.data.message`). Нужно там, где важно показать пользователю КОНКРЕТНУЮ
 * причину 4xx (роль занята, заявка уже подана, у вас уже есть команда), а не
 * общий тост `handleError` («Ошибка валидации»). Возвращает fallback, если
 * сообщения нет или это дефолтный axios «Request failed…».
 */
export function extractApiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data;
  if (data && typeof data === "object") {
    const rec = data as Record<string, unknown>;
    const m = rec["message"] ?? rec["error"] ?? rec["Message"];
    if (typeof m === "string" && m.trim().length > 0) return m.trim();
  }
  if (
    error instanceof Error &&
    error.message &&
    !/^Request failed with status code/i.test(error.message)
  ) {
    return error.message;
  }
  return fallback;
}

/**
 * KAN-208: HTTP-статус из axios-ошибки без `as any`. Возвращает undefined,
 * если это не ответ сервера (сетевой сбой, отмена, обычный Error).
 */
export function getApiErrorStatus(error: unknown): number | undefined {
  const status = (error as { response?: { status?: unknown } } | null)?.response
    ?.status;
  return typeof status === "number" ? status : undefined;
}

export type ErrorStage =
  | "no_connection"
  | "request_sent_no_response"
  | "response_error"
  | "auth_rejected"
  | "access_denied"
  | "not_found"
  | "server_error"
  | "validation_error"
  | "request_aborted"
  | "zod_error"
  | "react_error";

export type NormalizedApiError = {
  message: string;
  code?: string;
  status?: number;
  severity?: ErrorSeverity;
  details?: unknown;
  timestamp?: number;
  context?: string;
  location?: string | null;
  toastTitle?: string;
  stage?: ErrorStage;
  stageDescription?: string;
  method?: string;
  endpoint?: string;
  requestSent?: boolean;
  responseReceived?: boolean;
  /** KAN-310: серверные ошибки валидации по полям (ValidationProblemDetails) */
  fieldErrors?: ServerFieldErrors;
};

export type ErrorSeverity = "low" | "medium" | "high" | "critical";
export type ErrorType =
  | "network"
  | "timeout"
  | "http"
  | "validation"
  | "auth"
  | "unknown";

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  requestId?: string;
  status?: number;
  url?: string;
  method?: string;
  toast?: unknown;
  errorType?: ErrorType;
  meta?: Record<string, unknown>;
  [key: string]: unknown;
}

const ERROR_CONTEXT_TOP_LEVEL_KEYS = new Set([
  "component",
  "action",
  "userId",
  "requestId",
  "status",
  "url",
  "method",
  "toast",
  "errorType",
  "meta",
]);

const normalizeErrorContext = (context?: ErrorContext): ErrorContext | undefined => {
  if (!context) return undefined;

  const normalized: ErrorContext = {
    component: context.component,
    action: context.action,
    userId: context.userId,
    requestId: context.requestId,
    status: context.status,
    url: context.url,
    method: context.method,
    toast: context.toast,
    errorType: context.errorType,
  };

  const meta: Record<string, unknown> = { ...(context.meta ?? {}) };
  for (const [key, value] of Object.entries(context)) {
    if (!ERROR_CONTEXT_TOP_LEVEL_KEYS.has(key) && value !== undefined) {
      meta[key] = value;
    }
  }
  if (Object.keys(meta).length > 0) {
    normalized.meta = meta;
  }

  return normalized;
};

export function determineStage(
  error: unknown,
  overrides?: { status?: number; code?: string },
): { stage: ErrorStage; stageDescription: string } {
  const stageMessages = getErrorMessages().Stage;
  const t = (stage: ErrorStage) => stageMessages[stage] ?? stageMessages["response_error"]!;

  const raw = error as {
    request?: unknown;
    response?: { status?: number };
    code?: string;
  };
  const status = overrides?.status ?? raw.response?.status;
  // code бывает числом (DOMException, ошибки Tauri) — без String() падал сам обработчик (GlitchTip 155).
  const code = String(overrides?.code ?? raw.code ?? "").toLowerCase();

  if (code === "err_canceled" || code === "canceled" || code === "cancelled") {
    return { stage: "request_aborted", stageDescription: t("request_aborted") };
  }
  if (!raw.request && !raw.response) {
    return { stage: "no_connection", stageDescription: t("no_connection") };
  }
  if (raw.request && !raw.response) {
    return { stage: "request_sent_no_response", stageDescription: t("request_sent_no_response") };
  }
  if (status === 401) return { stage: "auth_rejected", stageDescription: t("auth_rejected") };
  if (status === 403) return { stage: "access_denied", stageDescription: t("access_denied") };
  if (status === 404) return { stage: "not_found", stageDescription: t("not_found") };
  if (status && status >= 500) return { stage: "server_error", stageDescription: t("server_error") };
  if (status === 400) return { stage: "validation_error", stageDescription: t("validation_error") };
  return { stage: "response_error", stageDescription: t("response_error") };
}

export function getToastTitle(error: NormalizedApiError, ctx?: ErrorContext): string {
  if (error.stage === "request_aborted") return "";

  const messages = getErrorMessages();
  const toast = messages.Toast;
  const codeMessages = messages.Code;

  let url = typeof ctx?.url === "string" ? ctx.url : error.endpoint ?? "";
  if (!url && error.context) {
    try {
      const parsed = JSON.parse(error.context) as { url?: string };
      url = parsed?.url ?? "";
    } catch {
      url = "";
    }
  }
  const method = (error.method ?? ctx?.method ?? "").toUpperCase();
  const status = error.status;
  const hasResponse = error.responseReceived === true || (status !== undefined && status !== 0);
  const isGet = /GET/i.test(method) || method === "";
  const isMutate = /POST|PUT|PATCH/i.test(method);
  const isDelete = /DELETE/i.test(method);
  const urlLower = url.toLowerCase();

  const domainKey = (): string | null => {
    if (urlLower.includes("/me") || urlLower.includes("/users/")) return isGet ? "profile_get" : "profile_save";
    if (urlLower.includes("cover") || urlLower.includes("avatar") || urlLower.includes("upload")) return isGet ? "cover_fail" : "cover_update";
    if (urlLower.includes("team") && !urlLower.includes("tournament")) return isGet ? "team_get" : urlLower.includes("join") || urlLower.includes("invite") ? "join_fail" : "team_save";
    if (urlLower.includes("tournament")) return isGet ? "tournament_get" : "tournament_save";
    if (urlLower.includes("register") || urlLower.includes("registration")) return "register_fail";
    if (urlLower.includes("scrim")) return isGet ? "scrims_get" : "scrims_save";
    if (urlLower.includes("achievement")) return "achievements_get";
    if (urlLower.includes("auth") || urlLower.includes("oauth") || urlLower.includes("login")) return "auth_fail";
    if (urlLower.includes("notification")) return isGet ? "notifications_get" : "notifications_save";
    if (urlLower.includes("crash-report")) return "crash_report_fail";
    return null;
  };

  if (!hasResponse && !error.requestSent) return toast["no_send"]!;
  if (!hasResponse && error.requestSent) {
    if (error.code === "ERR_NETWORK") return toast["connection_lost"]!;
    return toast["no_response"]!;
  }
  if (status === 401) {
    if (urlLower.includes("auth") || urlLower.includes("oauth") || urlLower.includes("login")) return toast["auth_fail"]!;
    return toast["reauth"]!;
  }
  if (status === 403) return toast["forbidden"]!;
  // Бизнес-валидация 400 на auth/верификации показывается формой ИНЛАЙН
  // (extractAuthError). Глобальный toast тут — дубль и визуальный шум → подавляем.
  if (
    status === 400 &&
    // «register» НЕ подавляем: auth-регистрация идёт через /auth/register (покрыта
    // подстрокой auth), а турнирная /tournaments/N/register должна показывать тост (KAN-309)
    (urlLower.includes("auth") ||
      urlLower.includes("email-code") ||
      urlLower.includes("login") ||
      urlLower.includes("password"))
  ) {
    return "";
  }
  // Типизированный код бэкенда важнее любого URL-guessing (KAN-309)
  if (error.code && codeMessages[error.code]) {
    return codeMessages[error.code]!;
  }
  // Конкретное сообщение бэкенда для бизнес-ошибок (409; 400 на мутациях;
  // «тихая 200» OperationResultDto — KAN-311) — лучше общей фразы.
  // Для GET-фейлов остаются доменные фолбэки ниже.
  if (
    (status === 409 ||
      error.code === "OPERATION_FAILED" ||
      (status === 400 && (isMutate || isDelete))) &&
    typeof error.message === "string" &&
    error.message.trim().length > 0 &&
    error.message.length < 120 &&
    !/^Request failed with status code/i.test(error.message)
  ) {
    return error.message;
  }
  if (status === 409) return toast["default"]!;
  if (status === 400) return toast["validation"]!;
  if (isDelete) return toast["delete_fail"]!;
  if (isGet) {
    const d = domainKey();
    if (d) return toast[d] ?? toast["get_fail"]!;
    return toast["get_fail"]!;
  }
  if (isMutate) {
    if (status && status >= 500) return toast["post_5xx"]!;
    const d = domainKey();
    if (d) return toast[d] ?? toast["post_reject"]!;
    return toast["post_reject"]!;
  }
  if (error.code === "ERR_NETWORK" && error.requestSent) return toast["connection_lost"]!;
  if (error.code === "ERR_NETWORK") return toast["no_send"]!;
  return toast["default"]!;
}

const extractStackLocation = (error: unknown): string | null => {
  if (!error || typeof error !== "object") return null;
  const stack = (error as { stack?: unknown }).stack;
  if (typeof stack !== "string") return null;
  const lines = stack.split("\n").map((line) => line.trim());
  for (const line of lines) {
    const match = line.match(/\(?([A-Za-z]:[^\s)]+):(\d+):(\d+)\)?$/);
    if (match) {
      const [, file, lineNo, colNo] = match;
      return `${file}:${lineNo}:${colNo}`;
    }
  }
  return null;
};

export class AppError extends Error {
  public readonly code: string;
  public readonly status?: number | undefined;
  public readonly severity: ErrorSeverity;
  public readonly context?: ErrorContext | undefined;
  public readonly timestamp: number;

  constructor(
    message: string,
    code: string = "UNKNOWN_ERROR",
    status?: number,
    severity: ErrorSeverity = "medium",
    context?: ErrorContext,
  ) {
    super(message);
    this.name = "AppError";
    this.code = code;
    this.status = status;
    this.severity = severity;
    this.context = context;
    this.timestamp = Date.now();
  }
}

const normalizeConflictCode = (raw: unknown) => {
  const value = String(raw ?? "").trim();
  return value.length > 0 ? value : undefined;
};

const readRecordString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

const DEV_OFFLINE_TOAST_ID = "dev-offline-backend";
const DEV_OFFLINE_MESSAGE =
  "Локальный dev без бекенда: API, БД и Redis недоступны. Запустите backend или используйте NEXT_PUBLIC_USE_MOCKS=true.";
let devOfflineToastShown = false;

export function normalizeAxiosError(
  error: unknown,
  context?: ErrorContext,
): NormalizedApiError {
  const normalizedContext = normalizeErrorContext(context);
  const fallback: NormalizedApiError = {
    message: translateUi("litUsername.neizvestnayaOshibka"),
    timestamp: Date.now(),
    ...(normalizedContext ? { context: JSON.stringify(normalizedContext) } : {}),
  };

  if (!error) return fallback;

  if (looksLikeZodError(error)) {
    const msg =
      error.issues.length > 0
        ? error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; ")
        : (error.message || translateUi("litErrorHandler.oshibkaValidatsiiOtveta"));
    return {
      message: msg,
      code: "ZOD_VALIDATION_ERROR",
      status: 400,
      timestamp: Date.now(),
      stage: "zod_error",
      stageDescription: getErrorMessages().Stage["zod_error"]!,
      requestSent: false,
      responseReceived: false,
      ...(normalizedContext ? { context: JSON.stringify(normalizedContext) } : {}),
    };
  }

  const maybeAxios = error as {
    message?: string;
    code?: string;
    response?: { status?: number; data?: unknown };
    request?: unknown;
    config?: { url?: string; method?: string };
  };

  const requestSent = Boolean(maybeAxios.request);
  const responseReceived = Boolean(maybeAxios.response);
  const method = normalizedContext?.method ?? maybeAxios.config?.method ?? "";
  const fullUrl = normalizedContext?.url ?? maybeAxios.config?.url ?? "";
  const endpoint =
    typeof fullUrl === "string" && fullUrl.startsWith("http")
      ? (() => {
        try {
          return new URL(fullUrl).pathname || fullUrl;
        } catch {
          return fullUrl;
        }
      })()
      : fullUrl;

  const status = maybeAxios.response?.status as number | undefined;
  const data = maybeAxios.response?.data;
  const dataRecord =
    typeof data === "object" && data !== null
      ? (data as Record<string, unknown>)
      : null;

  const messageFromData = dataRecord
    ? readRecordString(dataRecord["message"]) ??
    readRecordString(dataRecord["error"]) ??
    readRecordString(dataRecord["title"])
    : undefined;
  let message = messageFromData ?? maybeAxios.message ?? translateUi("litErrorHandler.oshibkaZaprosa");

  const codeFromData = dataRecord
    ? readRecordString(dataRecord["code"]) ??
    readRecordString(dataRecord["errorCode"])
    : undefined;
  let code = codeFromData ?? maybeAxios.code ?? "UNKNOWN_ERROR";

  // KAN-310: ValidationProblemDetails → конкретика по полям вместо общего тоста
  const fieldErrors =
    status === 400 ? extractServerFieldErrors(dataRecord) : null;
  if (fieldErrors) {
    message = summarizeFieldErrors(fieldErrors);
    code = "VALIDATION_FIELD_ERRORS";
  }

  if (status === 409) {
    const conflictCode = normalizeConflictCode(
      dataRecord ? dataRecord["errorCode"] || dataRecord["code"] : undefined,
    );
    if (conflictCode) {
      code = conflictCode;
      message = getErrorMessages().Conflict[conflictCode] ?? message;
    } else {
      message = translateUi("litErrorHandler.konflikt");
      code = "CONFLICT";
    }
  }

  let { stage, stageDescription } = determineStage(maybeAxios, { status, code });
  if (
    normalizedContext?.component === "ErrorBoundary" &&
    !requestSent &&
    !responseReceived
  ) {
    stage = "react_error";
    stageDescription = getErrorMessages().Stage["react_error"]!;
  }

  return {
    message,
    code,
    ...(status !== undefined ? { status } : {}),
    details:
      dataRecord &&
      (dataRecord["details"] || dataRecord["errors"] || dataRecord),
    timestamp: Date.now(),
    stage,
    stageDescription,
    method: method || undefined,
    endpoint: endpoint || undefined,
    requestSent,
    responseReceived,
    ...(fieldErrors ? { fieldErrors } : {}),
    ...(normalizedContext
      ? {
        context: JSON.stringify({
          ...normalizedContext,
          ...(fullUrl ? { url: fullUrl } : {}),
          ...(method ? { method } : {}),
        }),
      }
      : {}),
  };
}

// Централизованная обработка ошибок
export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorLog: NormalizedApiError[] = [];
  private maxLogSize = 100;

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Обрабатывает ошибку и показывает уведомление
   */
  handleError(error: unknown, context?: ErrorContext): NormalizedApiError {
    const normalizedContext = normalizeErrorContext(context);
    // Проверяем, является ли ошибка Zod ошибкой
    if (this.isZodError(error)) {
      const stackLocation = extractStackLocation(error);
      const messages = getErrorMessages();
      const normalizedError: NormalizedApiError = {
        message: (error as z.ZodError).message ?? translateUi("litErrorHandler.oshibkaValidatsii"),
        code: "ZOD_VALIDATION_ERROR",
        status: 400,
        severity: "medium",
        ...(normalizedContext && { context: JSON.stringify(normalizedContext) }),
        timestamp: Date.now(),
        location: stackLocation,
        stage: "zod_error",
        stageDescription: messages.Stage["zod_error"]!,
        toastTitle: messages.Toast["validation"]!,
      };
      this.logError(normalizedError);
      this.showErrorNotification(normalizedError);
      // KAN-1217: zod-ошибка разбора ОТВЕТА сервера — это поломка контракта API, а не
      // ввод пользователя (формы ловят ZodError сами и сюда его не доносят). Раньше
      // эта ветка возвращалась, ничего не отправив, и в GlitchTip было пусто, пока
      // витрина метрик на проде сыпала ошибками на каждом профиле.
      if (normalizedContext?.component === "TanStackQuery") {
        const issues = (error as z.ZodError).issues ?? [];
        Sentry.captureException(error, {
          // Группируем по месту в схеме без индексов массивов: «3.metrics.29.x» и
          // «10.metrics.0.x» — одна и та же поломка, а не сотня разных.
          fingerprint: [
            "zod-contract",
            issues[0]?.path.filter((p) => typeof p !== "number").join(".") ?? "root",
            issues[0]?.code ?? "unknown",
          ],
          extra: {
            issueCount: issues.length,
            firstIssues: issues.slice(0, 5).map((i) => ({ path: i.path.join("."), message: i.message })),
            action: normalizedContext.action,
          },
          tags: { stage: "zod_error" },
        });
      }
      return normalizedError;
    }

    const normalizedError = normalizeAxiosError(error, normalizedContext);
    const stackLocation = extractStackLocation(error);

    const withLocation = { ...normalizedError, location: stackLocation };
    withLocation.toastTitle = getToastTitle(withLocation, normalizedContext);
    this.logError(withLocation);
    if (withLocation.toastTitle) this.showErrorNotification(withLocation);

    // Параллельная отправка в GlitchTip — только то, что действительно похоже на
    // поломку. KAN-259: правило «server_error и выше» мимо себя пропускало
    // ожидаемые бизнес-ошибки — они приходят голым Error без request/response и
    // потому получают стадию no_connection. Разбор — в shouldReportError.
    if (shouldReportError(withLocation, error)) {
      Sentry.captureException(error instanceof Error ? error : new Error(withLocation.message), {
        extra: {
          stage: withLocation.stage,
          code: withLocation.code,
          status: withLocation.status,
          endpoint: withLocation.endpoint,
          method: withLocation.method,
          component: normalizedContext?.component,
          action: normalizedContext?.action,
          stageDescription: withLocation.stageDescription,
        },
        tags: {
          stage: withLocation.stage,
          error_code: withLocation.code ?? "unknown",
        },
      });
    }

    return withLocation;
  }

  /**
   * Обрабатывает ошибку без показа уведомления
   */
  handleSilentError(
    error: unknown,
    context?: ErrorContext,
  ): NormalizedApiError {
    const normalizedContext = normalizeErrorContext(context);
    const normalizedError = normalizeAxiosError(error, normalizedContext);
    const stackLocation = extractStackLocation(error);

    if (
      normalizedError.code === "ERR_CANCELED" ||
      String(normalizedError.message ?? "").toLowerCase() === "canceled" ||
      String(normalizedError.message ?? "").toLowerCase() === "cancelled"
    ) {
      return { ...normalizedError, location: stackLocation };
    }

    const isDev =
      typeof window !== "undefined" &&
      process.env.NODE_ENV !== "production";
    const isNetworkError =
      normalizedContext?.errorType === "network" ||
      normalizedContext?.errorType === "timeout" ||
      normalizedError.code === "ERR_NETWORK" ||
      (normalizedError.status === 0 && !normalizedError.code);

    if (isDev && isNetworkError) {
      if (!devOfflineToastShown) {
        devOfflineToastShown = true;
        toast.info(DEV_OFFLINE_MESSAGE, {
          id: DEV_OFFLINE_TOAST_ID,
          duration: 8000,
        });
      }
      return { ...normalizedError, location: stackLocation };
    }

    const withLocation = { ...normalizedError, location: stackLocation };
    this.logError(withLocation);
    return withLocation;
  }

  /**
   * Создает AppError из обычной ошибки
   */
  createAppError(
    message: string,
    code: string = "UNKNOWN_ERROR",
    status?: number,
    severity: ErrorSeverity = "medium",
    context?: ErrorContext,
  ): AppError {
    return new AppError(
      message,
      code,
      status,
      severity,
      normalizeErrorContext(context),
    );
  }

  /**
   * Обрабатывает ошибки валидации
   */
  handleValidationError(errors: string[]): AppError {
    const message =
      errors.length === 1
        ? errors[0] || translateUi("litErrorHandler.oshibkaValidatsii")
        : translateUi("litErrorHandler.oshibkiValidatsii", { errors: errors.join(", ") });

    return this.createAppError(message, "VALIDATION_ERROR", 400, "medium");
  }

  /**
   * Обрабатывает Zod ошибки валидации
   */
  handleZodError(zodError: z.ZodError, context?: ErrorContext): AppError {
    const errors = zodError.issues.map((issue) => {
      const path = issue.path.length > 0 ? `${issue.path.join(".")}: ` : "";
      return `${path}${issue.message}`;
    });

    const message =
      errors.length === 1
        ? errors[0] || translateUi("litErrorHandler.oshibkaValidatsii")
        : translateUi("litErrorHandler.oshibkiValidatsii", { errors: errors.join(", ") });

    return this.createAppError(
      message,
      "ZOD_VALIDATION_ERROR",
      400,
      "medium",
      context,
    );
  }

  /**
   * Обрабатывает Zod ошибки с группировкой по полям
   */
  handleZodFieldErrors(
    zodError: z.ZodError,
    context?: ErrorContext,
  ): {
    error: AppError;
    fieldErrors: Record<string, string>;
  } {
    const fieldErrors: Record<string, string> = {};

    zodError.issues.forEach((issue) => {
      const field = issue.path.join(".");
      fieldErrors[field] = issue.message;
    });

    const message = translateUi("litErrorHandler.oshibkiValidatsiiPoley");
    const error = this.createAppError(
      message,
      "ZOD_FIELD_VALIDATION_ERROR",
      400,
      "medium",
      context,
    );

    return { error, fieldErrors };
  }

  /**
   * Обрабатывает Zod ошибки для форм с показом уведомлений
   */
  handleZodFormError(
    zodError: z.ZodError,
    context?: ErrorContext,
  ): Record<string, string> {
    const { fieldErrors } = this.handleZodFieldErrors(zodError, context);

    // Показываем общее уведомление об ошибках валидации
    // Вне React-дерева next-intl недоступен — язык берём из lang документа (KAN-1009).
    toast.error(translateUi("litErrorHandler.ispravteOshibkiVForme"), {
      description: translateUi("litErrorHandler.proverteVydelennyePolya"),
      duration: 5000,
    });

    return fieldErrors;
  }

  /**
   * Проверяет, является ли ошибка Zod ошибкой
   */
  isZodError(error: unknown): error is z.ZodError {
    return looksLikeZodError(error);
  }

  /**
   * Извлекает сообщения об ошибках из Zod ошибки
   */
  extractZodErrorMessages(zodError: z.ZodError): string[] {
    return zodError.issues.map((issue) => {
      const path = issue.path.length > 0 ? `${issue.path.join(".")}: ` : "";
      return `${path}${issue.message}`;
    });
  }

  /**
   * Извлекает ошибки полей из Zod ошибки
   */
  extractZodFieldErrors(zodError: z.ZodError): Record<string, string> {
    const fieldErrors: Record<string, string> = {};

    zodError.issues.forEach((issue) => {
      const field = issue.path.join(".");
      fieldErrors[field] = issue.message;
    });

    return fieldErrors;
  }

  /**
   * Обрабатывает ошибки аутентификации
   */
  handleAuthError(error: unknown, context?: ErrorContext): AppError {
    const normalizedError = normalizeAxiosError(error, context);

    if (normalizedError.status === API_STATUS_CODES.UNAUTHORIZED) {
      return this.createAppError(
        translateUi("litErrorHandler.sessiyaIstekla"),
        "AUTH_EXPIRED",
        401,
        "high",
        context,
      );
    }

    if (normalizedError.status === API_STATUS_CODES.FORBIDDEN) {
      return this.createAppError(
        translateUi("litErrorHandler.nedostatochnoPrav"),
        "FORBIDDEN",
        403,
        "high",
        context,
      );
    }

    return this.createAppError(
      normalizedError.message,
      "AUTH_ERROR",
      normalizedError.status ?? undefined,
      "high",
      context,
    );
  }

  /**
   * Обрабатывает ошибки сети
   */
  handleNetworkError(error: unknown, context?: ErrorContext): AppError {
    const normalizedError = normalizeAxiosError(error, context);

    return this.createAppError(
      translateUi("litErrorHandler.oshibkaSeti"),
      "NETWORK_ERROR",
      normalizedError.status,
      "medium",
      context,
    );
  }

  /**
   * Логирует ошибку
   */
  private logError(error: NormalizedApiError): void {
    // Бизнес-4xx (валидация/доступ/не найдено/отмена) — ОЖИДАЕМЫЕ, обработанные
    // ошибки: они показываются тостом и не являются сбоями. В лог ошибок /
    // crash-report и во всплывающий виджет они попадать не должны — иначе на проде
    // вылезает панель на каждую 400 (напр. «Эта роль уже занята»). Логируем только
    // настоящие сбои (5xx, сеть, zod/react, неизвестное).
    const handledClientStage =
      error.stage === "validation_error" ||
      error.stage === "access_denied" ||
      error.stage === "auth_rejected" ||
      error.stage === "not_found" ||
      error.stage === "request_aborted";
    if (handledClientStage) {
      // KAN-312: обработанные 4xx не шлём как crash, но оставляем breadcrumb —
      // при последующем настоящем сбое в GlitchTip видна предыстория.
      if (error.stage !== "request_aborted") {
        Sentry.addBreadcrumb({
          category: "api.handled-4xx",
          level: "warning",
          message: `${error.status ?? ""} ${error.code ?? ""} ${error.endpoint ?? ""}`.trim(),
          data: { stage: error.stage },
        });
      }
      return;
    }

    this.errorLog.unshift(error);

    // Ограничиваем размер лога
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog = this.errorLog.slice(0, this.maxLogSize);
    }

    // Логируем в консоль в dev режиме.
    // 401/403 не считаем ошибкой уровня console.error: «не залогинен / нет прав» —
    // ожидаемый ответ, а красный лог создаёт ложное впечатление сбоя.
    const isExpectedAuth = error.status === 401 || error.status === 403;
    if (typeof window !== "undefined" && !isExpectedAuth) {
      const env = process.env["NEXT_PUBLIC_APP_ENV"] || process.env.NODE_ENV;
      if (env && env !== "production") {
        console.error(
          "[ErrorHandler]",
          error.status ? `${error.status}:` : "",
          error.code || "",
          error.message,
          error.context,
        );
      }
    }
  }

  /**
   * Показывает уведомление об ошибке
   */
  private showErrorNotification(error: NormalizedApiError): void {
    if (!error.responseReceived && ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"].includes(error.code ?? "")) {
      reportNetworkFailure(!!error.requestSent && !["GET", "HEAD", "OPTIONS"].includes((error.method ?? "GET").toUpperCase()));
      return;
    }
    const severity = this.getErrorSeverity(error);

    const title = error.toastTitle ?? error.message;
    const subtitle = error.stageDescription ?? error.message;
    const duration =
      severity === "critical"
        ? ERROR_HANDLING.CRITICAL_ERROR_DURATION
        : severity === "high"
          ? 8000
          : severity === "medium"
            ? ERROR_HANDLING.NORMAL_ERROR_DURATION
            : 3000;

    toast.error(title, { id: `error:${title}:${subtitle}`, description: title !== subtitle ? subtitle : undefined, duration });
  }

  /**
   * Определяет серьезность ошибки
   */
  private getErrorSeverity(error: NormalizedApiError): ErrorSeverity {
    if (error.status === 409) return "medium";
    if (
      error.status === API_STATUS_CODES.INTERNAL_SERVER_ERROR ||
      error.status === API_STATUS_CODES.SERVICE_UNAVAILABLE
    )
      return "critical";
    if (
      error.status === API_STATUS_CODES.UNAUTHORIZED ||
      error.status === API_STATUS_CODES.FORBIDDEN
    )
      return "high";
    if (
      error.status === API_STATUS_CODES.BAD_REQUEST ||
      error.status === API_STATUS_CODES.NOT_FOUND
    )
      return "medium";
    return "low";
  }

  /**
   * Получает историю ошибок
   */
  getErrorLog(): NormalizedApiError[] {
    return [...this.errorLog];
  }

  /**
   * Очищает историю ошибок
   */
  clearErrorLog(): void {
    this.errorLog = [];
  }
}

// Экспорт singleton instance
export const errorHandler = ErrorHandler.getInstance();

// Утилиты для удобства использования
export const handleError = (error: unknown, context?: ErrorContext) =>
  errorHandler.handleError(error, context);

export const handleSilentError = (error: unknown, context?: ErrorContext) =>
  errorHandler.handleSilentError(error, context);

// KAN-1004: отсюда удалены семь функций-обёрток над методами errorHandler
// (createAppError и шесть zod-утилит) — ни одну не звали, а сами методы
// класса на месте и доступны через errorHandler. Обёртка, которую никто
// не вызывает, выглядит как рекомендуемый способ работы с модулем.
